/* ==========================================================================
   SearcherFli > Renders
   ========================================================================== */
/* Import modules */
const { html } = require('common-tags');

const { addData, addStyle } = require('@bluekiri/alexandria/helpers/dataHelpers');

const Autocomplete = require('@bluekiri/alexandria-contrib-components/components/autocomplete-rxjs/template');
const Button = require('@bluekiri/alexandria-contrib-components/components/button-rxjs/template');
const Calendar = require('@bluekiri/alexandria-contrib-components/components/calendar-rxjs/template');
const Select = require('@bluekiri/alexandria-contrib-components/components/select-rxjs/template');
const Input = require('@bluekiri/alexandria-contrib-components/components/input-rxjs/template');
const Radio = require('@bluekiri/alexandria-contrib-components/components/radio-button-rxjs/template');
const Checkbox = require('@bluekiri/alexandria-contrib-components/components/checkbox-rxjs/template');
const Toggle = require('@bluekiri/alexandria-contrib-components/components/toggle-rxjs/template');

const { SINGLE, RANGE } = require('./client/js/calendar/constants');
const { ROUNDTRIP, ONEWAY, OPENJAW } = require('./client/js/product/constants');
const {
    PRODUCT_DEFAULT_VALUE,
    ORIGIN,
    DESTINATION,
    CALENDAR,
    PASSENGERS: { ADULTS, CHILDREN, BABIES }
} = require('./constants');

const { buildPassengersLabel } = require('./client/js/passengers/utils');

const configuration = require('./client/js/config');

/* Render roundtrip */
const renderRoundtrip = (product, literals, context) => {
    /* Literals */
    const { roundtripLabel = '' } = literals;

    return Radio(
        addData(
            {
                label: roundtripLabel,
                name: configuration().name.product,
                checked: product.toLowerCase() === ROUNDTRIP
            },
            { type: configuration().type.roundtrip }
        ),
        context
    );
};

/* Render oneway */
const renderOneway = (product, literals, context) => {
    /* Literals */
    const { onewayLabel = '' } = literals;

    return Radio(
        addData(
            {
                label: onewayLabel,
                name: configuration().name.product,
                checked: product.toLowerCase() === ONEWAY
            },
            { type: configuration().type.oneway }
        ),
        context
    );
};

/* Render openjaw */
const renderOpenjaw = (product, literals, context) => {
    /* Literals */
    const { openjawLabel = '' } = literals;

    return Radio(
        addData(
            {
                label: openjawLabel,
                name: configuration().name.product,
                checked: product.toLowerCase() === OPENJAW
            },
            { type: configuration().type.openjaw }
        ),
        context
    );
};

/* Render flight hotel */
const renderFlightHotel = (endpoints, literals, context) => {
    if (!endpoints.crossSelling) return '';

    /* Literals */
    const { crossSellingLabel = '' } = literals;

    return Toggle(
        addStyle(
            addData(
                {
                    label: crossSellingLabel
                },
                { type: configuration().type.crossSelling }
            ),
            ['searcher__item', 'is-bottom']
        ),
        context
    );
};

/* Render product */
const renderProduct = (product = PRODUCT_DEFAULT_VALUE, literals, context) => {
    return html(`
        ${renderRoundtrip(product, literals, context)}
        ${renderOneway(product, literals, context)}
        ${renderOpenjaw(product, literals, context)}
    `);
};

/* Render origin */
const renderOrigin = (origin, literals, context) => {
    /* Literals */
    const { originLabel = '' } = literals;

    return Autocomplete(
        addStyle(
            addData(
                {
                    config: {
                        literals
                    },
                    icon: 'marker',
                    label: originLabel,
                    types: ORIGIN.ORIGIN_DEFAULT_TYPES,
                    ...origin
                },
                { type: configuration().type.origin }
            ),
            ['searcher__item']
        ),
        context
    );
};

/* Render destination */
const renderDestination = (destination, literals, context) => {
    /* Literals */
    const { destinationLabel = '' } = literals;

    return Autocomplete(
        addStyle(
            addData(
                {
                    config: {
                        literals
                    },
                    icon: 'earth-planet',
                    label: destinationLabel,
                    types: DESTINATION.DESTINATION_DEFAULT_TYPES,
                    ...destination
                },
                { type: configuration().type.destination }
            ),
            ['searcher__item']
        ),
        context
    );
};

/* Render airlines */
const renderAirlines = (airlines, literals, context) => {
    if (!airlines) return '';

    /* Literals */
    const { airlinesLabel = '' } = literals;

    return Autocomplete(
        addStyle(
            addData(
                {
                    config: {
                        literals
                    },
                    icon: 'plane',
                    label: airlinesLabel,
                    ...airlines
                },
                { type: configuration().type.airlines }
            ),
            ['searcher__item', 'is-bottom']
        ),
        context
    );
};

/* Render calendar */
const renderCalendar = ({ calendar, product = PRODUCT_DEFAULT_VALUE }, literals, context) => {
    /* Literals */
    const { calendarLabel = '' } = literals;

    return Calendar(
        addStyle(
            addData(
                {
                    config: {
                        mode: product.toLowerCase() === ROUNDTRIP ? RANGE : SINGLE
                    },
                    icon: 'calendar',
                    label: calendarLabel,
                    startDate: CALENDAR.CALENDAR_DEFAULT_START_DATE,
                    endDate: CALENDAR.CALENDAR_DEFAULT_END_DATE,
                    minDate: CALENDAR.CALENDAR_DEFAULT_MIN_DATE,
                    maxDate: CALENDAR.CALENDAR_DEFAULT_MAX_DATE,
                    ...calendar
                },
                { type: configuration().type.calendar }
            ),
            ['searcher__item', 'searcher__item--large', 'is-active']
        ),
        context
    );
};

/* Render passengers */
const renderPassengers = (passengers, literals, context) => {
    /* Literals */
    const { passengerLabel = '' } = literals;

    const { adults = {}, children = {}, babies = {} } = passengers;
    const value = {
        adults: adults.value || ADULTS.ADULTS_DEFAULT_VALUE,
        children: children.value || CHILDREN.CHILDREN_DEFAULT_VALUE,
        babies: babies.value || BABIES.BABIES_DEFAULT_VALUE
    };

    return Input(
        addStyle(
            addData(
                {
                    icon: 'user',
                    label: passengerLabel,
                    type: 'button',
                    value: buildPassengersLabel(literals, value)
                },
                { type: configuration().type.passengers }
            ),
            ['searcher__item', 'is-active', configuration().css.passengers]
        ),
        context
    );
};

/* Render submit */
const renderSubmit = (literals, context) => {
    /* Literals */
    const { submitLabel = '' } = literals;

    return Button(
        addStyle(
            addData(
                {
                    content: submitLabel
                },
                { type: configuration().type.submit }
            ),
            ['searcher__button']
        ),
        context
    );
};

/* Render classes */
const renderClasses = (classes, literals, context) => {
    if (!classes.length) return '';

    /* Literals */
    const { classesLabel = 'Clases' } = literals;

    return Select(
        addStyle(
            addData(
                {
                    items: classes,
                    label: classesLabel,
                    config: {
                        choices: {
                            searchEnabled: false
                        }
                    }
                },
                { type: configuration().type.classes }
            ),
            ['searcher__item', 'is-bottom', configuration().css.classes]
        ),
        context
    );
};

/* Render discount */
const renderDiscount = ({ bonus }, literals, context) => {
    if (!bonus) return '';

    /* Literals */
    const { discountLabel = '' } = literals;

    const { items } = bonus;
    const { label } = items.reduce((cur, acc) => (cur.selected ? cur : acc), {});

    return Input(
        addStyle(
            addData(
                {
                    icon: 'promo-label',
                    type: 'button',
                    label: discountLabel,
                    value: label
                },
                { type: configuration().type.discount }
            ),
            ['searcher__item', 'is-bottom', 'is-active', configuration().css.discount]
        ),
        context
    );
};

/* Render options */
const renderOptions = (options, context) => {
    if (!options) return '';

    return options
        .map((item, index) => {
            return Checkbox(addData(item, { type: `${configuration().type.options}-${index}` }), context);
        })
        .join('');
};

module.exports = {
    renderProduct,
    renderOrigin,
    renderDestination,
    renderAirlines,
    renderCalendar,
    renderPassengers,
    renderSubmit,
    renderClasses,
    renderDiscount,
    renderOptions,
    renderFlightHotel
};
